/**
 * Who is signed in, and which budget year the screens are showing.
 *
 * `GET /me` is the gate's source of truth (docs/08-auth-integration.md): reaching it at all
 * means both auth layers held, so its answer carries the role, the department assignments and
 * the capabilities the nav needs — and the frontend never re-derives the permission matrix.
 */
import { createContext, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import type { Me, Meta } from '@/lib/types';

export interface Session {
  me: Me;
  meta: Meta;
  /**
   * The year the screens are showing.
   *
   * A selector, not a constant, because in January two years are live at once: R-24 gives a
   * grace window for claiming last year's events while this year's proposals are already
   * being raised.
   */
  year: number;
  setYear: (year: number) => void;
  /** Departments this person may raise a proposal for — manages ∪ sponsors. */
  ownDepartmentIds: number[];
  isHr: boolean;
}

const SessionContext = createContext<Session | null>(null);

export function useSession(): Session {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession outside a SessionProvider');
  return session;
}

export function SessionProvider({
  me,
  meta,
  children,
}: {
  me: Me;
  meta: Meta;
  children: ReactNode;
}) {
  const [year, setYear] = useState(() => Number(sgtYear()));

  const value = useMemo<Session>(() => {
    const own = [...new Set([...me.assignments.manages, ...me.assignments.sponsors])];
    return {
      me,
      meta,
      year,
      setYear,
      ownDepartmentIds: own,
      isHr: me.capabilities.hr,
    };
  }, [me, meta, year]);

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

/** The current year *in Singapore* — the app's own definition of when a year turns over. */
export function sgtYear(now: Date = new Date()): number {
  return Number(
    new Intl.DateTimeFormat('en-CA', { year: 'numeric', timeZone: 'Asia/Singapore' }).format(now),
  );
}

/** The two ambient facts, fetched once. Everything else hangs off the session. */
export function useIdentity() {
  const meta = useQuery({ queryKey: ['meta'], queryFn: api.meta, staleTime: Infinity });
  const me = useQuery({
    queryKey: ['me'],
    queryFn: api.me,
    // A refusal here is an answer about access, not a transient failure: retrying a 401 or a
    // `deactivated` just delays telling the user what happened.
    retry: false,
  });
  return { meta, me };
}
